import React from 'react';
import { TrendingUp, TrendingDown, Minus, MapPin, ArrowRight, IndianRupee, BarChart3, Activity } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { laneInsights, rateChartData } from '../mock/mockData';

const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

export default function Insights() {
  const maxRate = Math.max(...rateChartData.map(d => d.mum_del));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0B2545] font-[Poppins]">Rate Insights</h1>
        <p className="text-sm text-gray-500 mt-1">Real-time freight rate trends across India's top lanes.</p>
      </div>

      {/* Summary cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-3"><Activity className="w-5 h-5 text-[#00A651]" /><span className="text-sm text-gray-500">National Avg ₹/km</span></div>
          <p className="text-3xl font-extrabold text-[#0B2545] font-[Poppins]">₹42</p>
          <p className="text-xs text-[#00A651] mt-1">+3.2% vs last month</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-3"><BarChart3 className="w-5 h-5 text-[#0B2545]" /><span className="text-sm text-gray-500">Highest Rate Lane</span></div>
          <p className="text-xl font-extrabold text-[#0B2545] font-[Poppins]">Delhi → Jaipur</p>
          <p className="text-xs text-[#00A651] mt-1">₹78/km • +5.1%</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-5 h-5 text-[#B37800]" /><span className="text-sm text-gray-500">Most Active Corridor</span></div>
          <p className="text-xl font-extrabold text-[#0B2545] font-[Poppins]">Mumbai → Delhi</p>
          <p className="text-xs text-gray-500 mt-1">1,247 loads this week</p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-bold text-[#0B2545]">6-Month Rate Trend</h2>
            <p className="text-xs text-gray-500">Freight rates across top Indian corridors</p>
          </div>
          <div className="flex flex-wrap gap-3 text-xs">
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-[#00A651]"></div>Mumbai → Delhi</div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-[#0B2545]"></div>Bengaluru → Chennai</div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-[#FFB800]"></div>Ahmedabad → Mumbai</div>
          </div>
        </div>

        <div className="h-64 flex items-end justify-around gap-4">
          {rateChartData.map((d, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex items-end justify-center gap-1 h-52">
                <div className="w-1/3 bg-[#00A651] rounded-t transition-all" style={{ height: `${(d.mum_del/maxRate)*100}%` }} title={formatINR(d.mum_del)}></div>
                <div className="w-1/3 bg-[#0B2545] rounded-t transition-all" style={{ height: `${(d.bng_che/maxRate)*100}%` }} title={formatINR(d.bng_che)}></div>
                <div className="w-1/3 bg-[#FFB800] rounded-t transition-all" style={{ height: `${(d.ahm_mum/maxRate)*100}%` }} title={formatINR(d.ahm_mum)}></div>
              </div>
              <span className="text-xs text-gray-500 mt-1">{d.month}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Lane table */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h2 className="text-lg font-bold text-[#0B2545] mb-4">All Lane Insights</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 uppercase">
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 font-medium">Lane</th>
                <th className="text-right py-3 font-medium">Avg Rate</th>
                <th className="text-right py-3 font-medium">₹ / km</th>
                <th className="text-right py-3 font-medium hidden md:table-cell">Distance</th>
                <th className="text-right py-3 font-medium hidden md:table-cell">Volume</th>
                <th className="text-right py-3 font-medium">Trend</th>
              </tr>
            </thead>
            <tbody>
              {laneInsights.map((l, i) => {
                const Icon = l.trend === 'up' ? TrendingUp : l.trend === 'down' ? TrendingDown : Minus;
                const color = l.trend === 'up' ? 'text-[#00A651]' : l.trend === 'down' ? 'text-red-500' : 'text-gray-500';
                return (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-4 font-semibold text-[#0B2545]">{l.lane}</td>
                    <td className="py-4 text-right font-medium">{formatINR(l.avgRate)}</td>
                    <td className="py-4 text-right">₹{l.ratePerKm}</td>
                    <td className="py-4 text-right text-gray-500 hidden md:table-cell">{l.distance} km</td>
                    <td className="py-4 text-right hidden md:table-cell">
                      <Badge className={`border-0 text-xs ${l.volume === 'Very High' || l.volume === 'High' ? 'bg-[#00A651]/10 text-[#00A651]' : l.volume === 'Medium' ? 'bg-[#FFB800]/15 text-[#B37800]' : 'bg-gray-100 text-gray-600'}`}>{l.volume}</Badge>
                    </td>
                    <td className={`py-4 text-right font-medium ${color}`}>
                      <div className="inline-flex items-center gap-1">
                        <Icon className="w-3.5 h-3.5" />
                        {l.change > 0 ? '+' : ''}{l.change}%
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
